import clsx from 'clsx';
import { useNavigate } from 'react-router';
import { Avatar, Icon, Popover, useToast, type IconName } from '@/design-system';
import type { ReactNode } from 'react';
import { LANGUAGE_NAMES, ns, useT } from '@/i18n';
import { useFavouriteIds, useLogout, usePartnerStatus, useSession, useTrips } from '@/services/queries';
import { paths } from '../paths';
import { useOverlays } from '../state/overlays';
import { usePreferences } from '../state/preferences';
import { useUnreadCount } from './useUnread';
import styles from './AccountMenu.module.css';

function Item({
  icon,
  children,
  aside,
  onClick,
  strong,
}: {
  icon: IconName;
  children: string;
  aside?: ReactNode;
  onClick: () => void;
  strong?: boolean;
}) {
  return (
    <button type="button" role="menuitem" className={clsx(styles.item, strong && styles.strong)} onClick={onClick}>
      <Icon name={icon} size={19} />
      {children}
      {aside}
    </button>
  );
}

/** Account menu (`.pop`, screen 24): top-right on desktop, a bottom sheet on mobile. */
export function AccountMenu() {
  const navigate = useNavigate();
  const t = useT(ns.shell);
  const tc = useT(ns.common);
  const overlays = useOverlays();
  const { language, currency } = usePreferences();
  const { data: user } = useSession();
  const { data: trips } = useTrips(!!user);
  const { data: favIds } = useFavouriteIds();
  const { data: partner } = usePartnerStatus(!!user);
  const dashboard = partner?.status === 'approved' ? partner.dashboardUrl : null;
  const unread = useUnreadCount();
  const logout = useLogout();
  const toast = useToast();
  const go = (to: string) => {
    overlays.close();
    navigate(to);
  };
  const upcoming = (trips ?? []).filter((t) => t.status === 'pending' || t.status === 'confirmed').length;

  return (
    <Popover open={overlays.current === 'account'} onClose={overlays.close} label={t('menu.label')} role="menu">
      {user ? (
        <>
          <div className={styles.head}>
            <Avatar name={user.name} size={44} />
            <div className={styles.who}>
              <b>{user.name}</b>
              <span className="t-small t-muted">{user.email}</span>
            </div>
          </div>
          <div className="l-rule" />
          <div className={styles.group}>
            <Item
              icon="briefcase"
              onClick={() => go(paths.trips)}
              aside={<span className={styles.aside}>{t.rich('menu.upcoming', { count: upcoming })}</span>}
            >
              {t('menu.bookings')}
            </Item>
            <Item
              icon="heart"
              onClick={() => go(paths.favourites)}
              aside={<span className={styles.aside}>{favIds?.length ?? 0}</span>}
            >
              {tc('favourites')}
            </Item>
            <Item
              icon="chat"
              onClick={() => go(paths.messages)}
              aside={unread ? <span className={styles.count}>{unread}</span> : undefined}
            >
              {tc('messages')}
            </Item>
            <Item icon="user" onClick={() => go(paths.profile)}>
              {tc('profile')}
            </Item>
          </div>
          <div className={clsx('l-rule', styles.rule)} />
        </>
      ) : (
        <>
          <div className={styles.guestGroup}>
            <Item icon="user" strong onClick={() => go(paths.login)}>
              {tc('signIn')}
            </Item>
            <Item icon="plus" onClick={() => go(paths.register)}>
              {tc('signUp')}
            </Item>
          </div>
          <div className={clsx('l-rule', styles.ruleTop)} />
        </>
      )}
      <Item
        icon="globe"
        onClick={() => overlays.open('preferences')}
        aside={<span className={styles.aside}>{`${LANGUAGE_NAMES[language]} · ${currency}`}</span>}
      >
        {t('preferences')}
      </Item>
      {dashboard ? (
        <Item
          icon="briefcase"
          onClick={() => {
            overlays.close();
            window.open(dashboard, '_blank', 'noopener');
          }}
          aside={<span className={styles.aside}>{t('menu.agency')}</span>}
        >
          {t('agencyMode')}
        </Item>
      ) : (
        <Item
          icon="briefcasePlus"
          onClick={() => go(paths.partner)}
          aside={<span className={styles.aside}>{t('menu.agencies')}</span>}
        >
          {t('becomePartner')}
        </Item>
      )}
      <Item icon="headset" onClick={() => go(paths.contact)} aside={<span className={styles.aside}>24/7</span>}>
        {t('menu.support')}
      </Item>
      {user && (
        <>
          <div className={clsx('l-rule', styles.rule)} />
          <Item
            icon="logout"
            onClick={() => {
              overlays.close();
              logout.mutate(undefined, { onSuccess: () => toast.show(t('menu.signedOut')) });
            }}
          >
            {t('menu.signOut')}
          </Item>
        </>
      )}
    </Popover>
  );
}
